import { useState, useEffect, useRef, useCallback } from 'react';
import Navigation from '@/components/Navigation';
import { Home, Check, Truck, Warehouse, Droplets, Move, Pencil, Square, Circle, Type, Eraser, Trash2, Save, Download, Undo, Redo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/App';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const DRAWING_TOOLS = {
  SELECT: 'select',
  PEN: 'pen',
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  TEXT: 'text',
  ERASER: 'eraser'
};

const COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Brown', value: '#8B4513' },
  { name: 'Green', value: '#228B22' },
  { name: 'Blue', value: '#1E90FF' },
  { name: 'Red', value: '#DC143C' },
  { name: 'Gray', value: '#808080' }
];

// Draw grid function - outside component to prevent recreation
const drawGrid = (ctx) => {
  ctx.strokeStyle = '#f0f0f0'; // Very light gray
  ctx.lineWidth = 0.5; // Thin lines
  
  // Draw grid with 20px spacing (smaller, more detailed)
  for (let x = 0; x < ctx.canvas.width; x += 20) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, ctx.canvas.height);
    ctx.stroke();
  }
  
  for (let y = 0; y < ctx.canvas.height; y += 20) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(ctx.canvas.width, y);
    ctx.stroke();
  }
};

const FacilityBuilder = ({ user, logout }) => {
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shelterRequirements, setShelterRequirements] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('location');
  const canvasRef = useRef(null);
  const canvasInitializedRef = useRef(false); // Track if canvas has been initialized with saved data
  
  // Tab order for navigation
  const tabOrder = ['location', 'corral', 'resources', 'transport', 'layout'];
  
  const goToNextTab = () => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex < tabOrder.length - 1) {
      setActiveTab(tabOrder[currentIndex + 1]);
    }
  };
  
  const goToPrevTab = () => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabOrder[currentIndex - 1]);
    }
  };
  
  const isLastTab = () => activeTab === tabOrder[tabOrder.length - 1];
  const isFirstTab = () => activeTab === tabOrder[0];
  
  // Drawing states
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState(DRAWING_TOOLS.PEN);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(2);
  const [showGrid, setShowGrid] = useState(true); // Grid toggle state
  const [history, setHistory] = useState([[]]);
  const [historyStep, setHistoryStep] = useState(0);
  const [startPos, setStartPos] = useState(null);
  const [tempCanvas, setTempCanvas] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [textPosition, setTextPosition] = useState(null);
  // Layer-based system - everything is a layer
  const [layers, setLayers] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentPath, setCurrentPath] = useState([]);
  const isUndoRedoRef = useRef(false);

  // Define renderLayers BEFORE useEffects to avoid "uninitialized variable" error
  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback((screenX, screenY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    
    // Step 1: Convert screen coordinates to canvas element coordinates
    let x = screenX - rect.left;
    let y = screenY - rect.top;
    
    // Step 2: Account for CSS scaling (canvas display size vs internal size)
    // The canvas element might be displayed at a different size than its internal resolution
    x = x * (canvas.width / rect.width);
    y = y * (canvas.height / rect.height);
    
    return { x, y };
  }, []);

  const renderLayers = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Clear entire canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw all layers in order (BEFORE grid, so grid stays on top)
    layers.forEach((layer) => {
      const isSelected = selectedLayer && selectedLayer.id === layer.id;
      
      if (layer.type === 'path') {
        // Draw pen/eraser strokes
        if (layer.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(layer.points[0].x, layer.points[0].y);
          for (let i = 1; i < layer.points.length; i++) {
            ctx.lineTo(layer.points[i].x, layer.points[i].y);
          }
          ctx.strokeStyle = layer.color;
          ctx.lineWidth = layer.lineWidth;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
          
          // Selection highlight for paths
          if (isSelected) {
            ctx.strokeStyle = '#6FCFD3';
            ctx.lineWidth = layer.lineWidth + 2;
            ctx.stroke();
          }
        }
      } else if (layer.type === 'rectangle') {
        ctx.strokeStyle = isSelected ? '#6FCFD3' : layer.color;
        ctx.lineWidth = isSelected ? 3 : layer.lineWidth;
        ctx.strokeRect(layer.x, layer.y, layer.width, layer.height);
      } else if (layer.type === 'circle') {
        ctx.strokeStyle = isSelected ? '#6FCFD3' : layer.color;
        ctx.lineWidth = isSelected ? 3 : layer.lineWidth;
        ctx.beginPath();
        ctx.arc(layer.x, layer.y, layer.radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (layer.type === 'text') {
        ctx.font = `${layer.size * 8}px Arial`;
        ctx.fillStyle = layer.color;
        ctx.fillText(layer.text, layer.x, layer.y);
        
        // Selection highlight for text
        if (isSelected) {
          ctx.strokeStyle = '#6FCFD3';
          ctx.lineWidth = 2;
          const metrics = ctx.measureText(layer.text);
          const textHeight = layer.size * 8;
          ctx.strokeRect(
            layer.x - 5,
            layer.y - textHeight + 2,
            metrics.width + 10,
            textHeight + 5
          );
        }
      }
    });
    
    // Draw grid LAST so it always appears on top (won't be erased)
    if (showGrid) drawGrid(ctx);
  }, [layers, selectedLayer, showGrid]);

  useEffect(() => {
    fetchFacility();
  }, []);

  useEffect(() => {
    if (facility?.state) {
      fetchShelterRequirements(facility.state);
    }
  }, [facility?.state]);

  // Track if user has made unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Direct render function that doesn't depend on state
  const renderLayersDirectly = useCallback((layersToRender, canvas) => {
    if (!canvas) {
      canvas = canvasRef.current;
    }
    if (!canvas) {
      console.log('[FacilityBuilder] renderLayersDirectly: No canvas available');
      return;
    }
    
    const ctx = canvas.getContext('2d');
    
    // Clear entire canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw all layers
    layersToRender.forEach((layer) => {
      if (layer.type === 'path') {
        if (layer.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(layer.points[0].x, layer.points[0].y);
          for (let i = 1; i < layer.points.length; i++) {
            ctx.lineTo(layer.points[i].x, layer.points[i].y);
          }
          ctx.strokeStyle = layer.color;
          ctx.lineWidth = layer.lineWidth;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
        }
      } else if (layer.type === 'rectangle') {
        ctx.strokeStyle = layer.color;
        ctx.lineWidth = layer.lineWidth;
        ctx.strokeRect(layer.x, layer.y, layer.width, layer.height);
      } else if (layer.type === 'circle') {
        ctx.strokeStyle = layer.color;
        ctx.lineWidth = layer.lineWidth;
        ctx.beginPath();
        ctx.arc(layer.x, layer.y, layer.radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (layer.type === 'text') {
        ctx.font = `${layer.size * 8}px Arial`;
        ctx.fillStyle = layer.color;
        ctx.fillText(layer.text, layer.x, layer.y);
      }
    });
    
    // Draw grid on top if enabled
    if (showGrid) drawGrid(ctx);
    
    console.log('[FacilityBuilder] Directly rendered', layersToRender.length, 'layers');
  }, [showGrid]);

  // Initialize canvas and load saved layers when tab becomes active
  // Using a single effect that handles both initialization and data loading
  useEffect(() => {
    // Only process when on the layout tab
    if (activeTab !== 'layout') {
      return;
    }
    
    // Wait for next frame to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) {
        console.log('[FacilityBuilder] Canvas not yet in DOM');
        return;
      }
      
      // Initialize canvas dimensions if needed
      if (canvas.width === 0 || canvas.width === 300) { // 300 is default canvas width
        const container = canvas.parentElement;
        const containerWidth = container?.clientWidth || window.innerWidth - 64;
        const maxWidth = Math.min(containerWidth, 1200);
        const aspectRatio = 16 / 10;
        
        canvas.width = maxWidth;
        canvas.height = maxWidth / aspectRatio;
        console.log('[FacilityBuilder] Canvas initialized:', maxWidth, 'x', (maxWidth / aspectRatio));
      }
      
      // Clear canvas and draw grid
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (showGrid) drawGrid(ctx);
      
      // Load saved layers if available and not already loaded
      if (facility?.layout?.layers && 
          facility.layout.layers.length > 0 && 
          !canvasInitializedRef.current &&
          !hasUnsavedChanges) {
        
        console.log('[FacilityBuilder] Loading saved layers:', facility.layout.layers.length);
        const savedLayers = facility.layout.layers;
        setLayers(savedLayers);
        canvasInitializedRef.current = true;
        
        // Render immediately since we have the canvas
        renderLayersDirectly(savedLayers, canvas);
      } else if (layers.length > 0) {
        // Re-render existing layers (e.g., when switching tabs back)
        renderLayersDirectly(layers, canvas);
      }
    }, 50); // Small delay to ensure DOM is ready
    
    return () => clearTimeout(timeoutId);
  }, [activeTab, facility, showGrid, hasUnsavedChanges, renderLayersDirectly, layers]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      // Save current drawing
      const imageData = canvas.toDataURL();
      
      // Get new dimensions
      const container = canvas.parentElement;
      const containerWidth = container?.clientWidth || window.innerWidth - 64;
      const maxWidth = Math.min(containerWidth, 1200);
      const aspectRatio = 16 / 10;
      
      canvas.width = maxWidth;
      canvas.height = maxWidth / aspectRatio;
      
      // Restore drawing
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (showGrid) drawGrid(ctx);
      
      if (imageData) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          renderLayers();
        };
        img.src = imageData;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Re-render canvas when layers change or grid toggle changes
  useEffect(() => {
    // Only render if we're on the layout tab and have layers
    if (activeTab !== 'layout' || layers.length === 0) return;
    
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0) return;
    
    console.log('[FacilityBuilder] Render effect triggered, layers:', layers.length);
    renderLayers();
  }, [layers, renderLayers, showGrid, activeTab]);

  // Save to history when layers change (except initial load and undo/redo)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (!isUndoRedoRef.current && (layers.length > 0 || history.length > 1)) {
      saveToHistory();
    }
    // Reset the flag after processing
    isUndoRedoRef.current = false;
  }, [layers]);

  // Moved outside useCallback to prevent recreation

  const loadDrawing = (drawingData) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      saveToHistory();
      renderLayers();
    };
    img.src = drawingData;
  };

  const saveToHistory = () => {
    // Save current layers state to history
    const newHistory = [...history.slice(0, historyStep + 1), JSON.parse(JSON.stringify(layers))];
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  };

  const undo = () => {
    if (historyStep > 0) {
      const newStep = historyStep - 1;
      setHistoryStep(newStep);
      isUndoRedoRef.current = true;
      setLayers(JSON.parse(JSON.stringify(history[newStep])));
      setSelectedLayer(null);
    }
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      const newStep = historyStep + 1;
      setHistoryStep(newStep);
      isUndoRedoRef.current = true;
      setLayers(JSON.parse(JSON.stringify(history[newStep])));
      setSelectedLayer(null);
    }
  };

  const clearCanvas = () => {
    if (!window.confirm('Clear everything on the canvas?')) return;
    setLayers([]);
    setHasUnsavedChanges(true);
    setSelectedLayer(null);
    // Reset initialized flag since user is starting fresh
    canvasInitializedRef.current = true; // Mark as "initialized" so it doesn't reload from DB
  };

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches && e.touches.length > 0) {
      return screenToCanvas(e.touches[0].clientX, e.touches[0].clientY);
    } else {
      return screenToCanvas(e.clientX, e.clientY);
    }
  };

  const getLayerAtPosition = (x, y) => {
    // Check from top to bottom (reverse order so top layers are checked first)
    for (let i = layers.length - 1; i >= 0; i--) {
      const layer = layers[i];
      
      if (layer.type === 'rectangle') {
        // Handle both positive and negative width/height
        const minX = Math.min(layer.x, layer.x + layer.width);
        const maxX = Math.max(layer.x, layer.x + layer.width);
        const minY = Math.min(layer.y, layer.y + layer.height);
        const maxY = Math.max(layer.y, layer.y + layer.height);
        
        // Add padding for easier selection (20px tolerance)
        const padding = 20;
        if (
          x >= minX - padding &&
          x <= maxX + padding &&
          y >= minY - padding &&
          y <= maxY + padding
        ) {
          console.log('✅ Selected rectangle');
          return layer;
        }
      } else if (layer.type === 'circle') {
        const distance = Math.sqrt(Math.pow(x - layer.x, 2) + Math.pow(y - layer.y, 2));
        // Add padding for easier selection (20px tolerance)
        const hitRadius = layer.radius + 20;
        if (distance <= hitRadius) {
          console.log('✅ Selected circle');
          return layer;
        }
      } else if (layer.type === 'text') {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.font = `${layer.size * 8}px Arial`;
        const metrics = ctx.measureText(layer.text);
        const textHeight = layer.size * 8;
        
        if (
          x >= layer.x - 5 &&
          x <= layer.x + metrics.width + 5 &&
          y >= layer.y - textHeight &&
          y <= layer.y + 5
        ) {
          console.log('✅ Selected text');
          return layer;
        }
      } else if (layer.type === 'path') {
        // Check if click is near any point in the path (within 15px)
        for (let point of layer.points) {
          const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
          if (distance <= 15) {
            console.log('✅ Selected path');
            return layer;
          }
        }
      }
    }
    return null;
  };

  const startDrawing = (e) => {
    e.preventDefault();
    
    const pos = getMousePos(e);
    
    // SELECT tool - pick any layer
    if (currentTool === DRAWING_TOOLS.SELECT) {
      const clickedLayer = getLayerAtPosition(pos.x, pos.y);
      if (clickedLayer) {
        setSelectedLayer(clickedLayer);
        setIsDragging(true);
        
        // Calculate offset for dragging
        if (clickedLayer.type === 'path') {
          // For paths, use first point as reference
          setDragOffset({
            x: pos.x - clickedLayer.points[0].x,
            y: pos.y - clickedLayer.points[0].y
          });
        } else {
          setDragOffset({
            x: pos.x - clickedLayer.x,
            y: pos.y - clickedLayer.y
          });
        }
        console.log('Selected layer:', clickedLayer.type, clickedLayer.id);
        return;
      }
      
      // Clear selection if clicking empty space
      setSelectedLayer(null);
      return;
    }
    
    // Drawing tools
    setSelectedLayer(null);
    setIsDrawing(true);
    setStartPos(pos);

    if (currentTool === DRAWING_TOOLS.PEN) {
      setCurrentPath([pos]);
    } else if (currentTool === DRAWING_TOOLS.ERASER) {
      setCurrentPath([pos]);
    } else if (currentTool === DRAWING_TOOLS.TEXT) {
      setTextPosition(pos);
      setShowTextInput(true);
    }
  };

  // Removed - now using getLayerAtPosition instead

  const draw = (e) => {
    e.preventDefault();
    
    const pos = getMousePos(e);
    
    // Handle dragging any layer
    if (isDragging && selectedLayer) {
      const newX = pos.x - dragOffset.x;
      const newY = pos.y - dragOffset.y;
      
      setLayers(layers.map(layer => {
        if (layer.id === selectedLayer.id) {
          if (layer.type === 'path') {
            // Move all points in the path
            const deltaX = newX - layer.points[0].x;
            const deltaY = newY - layer.points[0].y;
            return {
              ...layer,
              points: layer.points.map(p => ({ x: p.x + deltaX, y: p.y + deltaY }))
            };
          } else {
            return { ...layer, x: newX, y: newY };
          }
        }
        return layer;
      }));
      
      setSelectedLayer(prev => {
        if (prev.type === 'path') {
          const deltaX = newX - prev.points[0].x;
          const deltaY = newY - prev.points[0].y;
          return {
            ...prev,
            points: prev.points.map(p => ({ x: p.x + deltaX, y: p.y + deltaY }))
          };
        }
        return { ...prev, x: newX, y: newY };
      });
      return;
    }
    
    if (!isDrawing) return;

    if (currentTool === DRAWING_TOOLS.PEN || currentTool === DRAWING_TOOLS.ERASER) {
      // Add point to current path
      setCurrentPath(prev => [...prev, pos]);
      
      // Draw preview
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Redraw everything
      renderLayers();
      
      // Draw current path being drawn
      if (currentPath.length > 0) {
        ctx.beginPath();
        ctx.moveTo(currentPath[0].x, currentPath[0].y);
        for (let i = 1; i < currentPath.length; i++) {
          ctx.lineTo(currentPath[i].x, currentPath[i].y);
        }
        ctx.lineTo(pos.x, pos.y);
        ctx.strokeStyle = currentTool === DRAWING_TOOLS.ERASER ? '#ffffff' : currentColor;
        ctx.lineWidth = currentTool === DRAWING_TOOLS.ERASER ? brushSize * 4 : brushSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
      }
      
      // Redraw grid on top if it's enabled (so eraser doesn't remove grid)
      if (showGrid) drawGrid(ctx);
    }
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    
    if (isDragging) {
      setIsDragging(false);
      return;
    }
    
    if (!isDrawing) return;
    
    if (currentTool === DRAWING_TOOLS.PEN || currentTool === DRAWING_TOOLS.ERASER) {
      // Save path as a layer
      if (currentPath.length > 1) {
        const newLayer = {
          id: Date.now(),
          type: 'path',
          points: [...currentPath],
          color: currentTool === DRAWING_TOOLS.ERASER ? '#ffffff' : currentColor,
          lineWidth: currentTool === DRAWING_TOOLS.ERASER ? brushSize * 4 : brushSize
        };
        setLayers(prev => [...prev, newLayer]);
        setHasUnsavedChanges(true);
      }
      setCurrentPath([]);
    }
    
    setIsDrawing(false);
  };

  // Removed - no longer needed with layer system

  const addText = () => {
    if (!textInput.trim() || !textPosition) return;
    
    const newLayer = {
      id: Date.now(),
      type: 'text',
      text: textInput,
      x: textPosition.x,
      y: textPosition.y,
      color: currentColor,
      size: brushSize
    };
    
    setLayers(prev => [...prev, newLayer]);
    setHasUnsavedChanges(true);
    setTextInput('');
    setShowTextInput(false);
    setTextPosition(null);
    console.log('Added text layer');
  };

  const deleteSelectedLayer = () => {
    if (!selectedLayer) return;
    setLayers(layers.filter(layer => layer.id !== selectedLayer.id));
    setHasUnsavedChanges(true);
    setSelectedLayer(null);
  };

  const saveLayoutDrawing = async () => {
    try {
      const canvas = canvasRef.current;
      const drawingData = canvas.toDataURL();
      console.log('[FacilityBuilder] Saving layout with layers:', layers.length);
      await api.post('/facility/update', {
        layout: { 
          drawing: drawingData,
          layers: layers
        }
      });
      setHasUnsavedChanges(false);
      // Mark as initialized since we just saved
      canvasInitializedRef.current = true;
      toast.success('Layout saved successfully!');
      // Refresh facility data to confirm save
      fetchFacility();
    } catch (error) {
      console.error('Error saving layout:', error);
      toast.error('Failed to save layout');
    }
  };

  const downloadImage = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = 'facility-layout.png';
    link.href = canvas.toDataURL();
    link.click();
  };

  const fetchFacility = async () => {
    try {
      const response = await api.get('/facility/get');
      console.log('[FacilityBuilder] Fetched facility:', response.data);
      console.log('[FacilityBuilder] Layout:', response.data?.layout);
      console.log('[FacilityBuilder] Layers count:', response.data?.layout?.layers?.length || 0);
      setFacility(response.data);
    } catch (error) {
      console.error('Error fetching facility:', error);
    }
  };

  const fetchShelterRequirements = async (state) => {
    try {
      const response = await api.get(`/facility/shelter-requirements/${state}`);
      setShelterRequirements(response.data);
    } catch (error) {
      console.error('Error fetching shelter requirements:', error);
    }
  };

  const updateFacility = async (updates) => {
    setLoading(true);
    try {
      const response = await api.post('/facility/update', updates);
      setFacility(response.data);
      if (response.data.qualified) {
        toast.success('Congratulations! Your facility now meets all adoption requirements!');
      }
    } catch (error) {
      toast.error('Failed to update facility');
    } finally {
      setLoading(false);
    }
  };

  // Save facility and mark as complete
  const saveFacility = async () => {
    setLoading(true);
    try {
      // Save the current facility state
      const response = await api.post('/facility/update', facility);
      setFacility(response.data);
      
      if (response.data.qualified) {
        toast.success('Facility saved and qualified for adoption! You can now adopt mustangs.');
      } else {
        toast.success('Facility saved! Complete all requirements to qualify for adoption.');
      }
    } catch (error) {
      console.error('Error saving facility:', error);
      toast.error('Failed to save facility');
    } finally {
      setLoading(false);
    }
  };

  // Old drawing functions removed

  if (!facility) return null;

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-8">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-8 fade-in" style={{color: '#D66238'}}>
          Facility Builder
        </h1>

        {/* Qualification Status */}
        <div
          data-testid="qualification-status"
          className="p-4 sm:p-6 rounded-2xl mb-4 sm:mb-8"
          style={{backgroundColor: facility.qualified ? '#6FCFD3' : '#D66238', color: '#fff'}}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                {facility.qualified ? (
                  <Check className="w-6 h-6" />
                ) : (
                  <Home className="w-6 h-6" />
                )}
                <h3 className="text-xl font-bold">
                  {facility.qualified ? 'Facility Qualified!' : 'Complete Your Facility'}
                </h3>
              </div>
              <p>
                {facility.qualified
                  ? 'Your facility now meets all adoption requirements.'
                  : 'Complete all required specifications to qualify for mustang adoption.'}
              </p>
            </div>
            
            {/* Continue Adoption Process Button */}
            {facility.qualified && (
              <Button
                onClick={() => window.location.href = '/virtual-corral'}
                className="bg-white hover:bg-gray-100 text-lg font-semibold px-6 py-3 whitespace-nowrap transition-all shadow-lg hover:shadow-xl"
                style={{color: '#6FCFD3'}}
              >
                Continue Adoption Process →
              </Button>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4 sm:mb-8 grid grid-cols-3 sm:grid-cols-5 h-auto">
            <TabsTrigger value="location" className="text-xs sm:text-sm py-2">Location</TabsTrigger>
            <TabsTrigger value="corral" className="text-xs sm:text-sm py-2">Corral</TabsTrigger>
            <TabsTrigger value="resources" className="text-xs sm:text-sm py-2">Resources</TabsTrigger>
            <TabsTrigger value="transport" className="text-xs sm:text-sm py-2">Transport</TabsTrigger>
            <TabsTrigger value="layout" className="text-xs sm:text-sm py-2 col-span-3 sm:col-span-1">Layout</TabsTrigger>
          </TabsList>

          {/* LOCATION TAB */}
          <TabsContent value="location">
            <div className="space-y-6">
              {/* State Selection */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                <h2 className="text-2xl font-bold mb-6" style={{color: '#D66238'}}>Location Information</h2>
                <div data-testid="state-select">
                  <Label className="text-base mb-2 block" style={{color: '#D66238'}}>State *</Label>
                  <Select
                    value={facility.state || ''}
                    onValueChange={(value) => updateFacility({ state: value })}
                  >
                    <SelectTrigger className="max-w-xs">
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Shelter Requirements */}
              {shelterRequirements && (
                <div className="p-6 rounded-2xl" style={{backgroundColor: '#6FCFD3', color: '#fff'}}>
                  <h3 className="text-xl font-bold mb-3">Shelter Requirements for {shelterRequirements.state}</h3>
                  <p className="mb-3">{shelterRequirements.description}</p>
                  {shelterRequirements.additional && (
                    <p className="mb-3 font-semibold">⚠️ {shelterRequirements.additional}</p>
                  )}
                  <div className="space-y-1">
                    <p className="font-semibold">Acceptable shelter types:</p>
                    <ul className="list-disc list-inside">
                      {shelterRequirements.shelter_types.map((type, idx) => (
                        <li key={idx}>{type}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Shelter Type Selection */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                <h3 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>Select Your Shelter Type *</h3>
                <Select
                  value={facility.shelter_type || ''}
                  onValueChange={(value) => updateFacility({ shelter_type: value })}
                  disabled={!shelterRequirements}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select shelter type" />
                  </SelectTrigger>
                  <SelectContent>
                    {shelterRequirements?.shelter_types.map((type, idx) => (
                      <SelectItem key={idx} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex justify-end mt-6">
              <button
                onClick={goToNextTab}
                className="px-6 py-3 rounded-full font-semibold text-white transition-all hover:scale-105"
                style={{ backgroundColor: '#D66238' }}
              >
                Next: Corral →
              </button>
            </div>
          </TabsContent>

          {/* CORRAL SPECS TAB */}
          <TabsContent value="corral">
            <div className="space-y-6">
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                <h2 className="text-2xl font-bold mb-6" style={{color: '#D66238'}}>Corral Construction Specifications</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Corral Material */}
                  <div>
                    <Label className="text-base mb-2 block" style={{color: '#D66238'}}>Corral Material *</Label>
                    <Select
                      value={facility.corral_material || ''}
                      onValueChange={(value) => updateFacility({ corral_material: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pipe-panels">Pipe Panels</SelectItem>
                        <SelectItem value="wood-posts-planks">Wood Posts and Planks (min 1.5" thick)</SelectItem>
                        <SelectItem value="horse-fence-vmesh">Horse Fence (V Mesh)</SelectItem>
                        <SelectItem value="horse-fence-square">Horse Fence (2" x 4" square)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs mt-2" style={{color: '#D66238', opacity: 0.7}}>
                      NOT approved: Barbed wire, large-mesh woven, stranded, electric
                    </p>
                  </div>

                  {/* Corral Dimensions */}
                  <div>
                    <Label className="text-base mb-2 block" style={{color: '#D66238'}}>Corral Dimensions *</Label>
                    <Select
                      value={facility.corral_dimensions || ''}
                      onValueChange={(value) => updateFacility({ corral_dimensions: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select dimensions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20x20">20' x 20' (400 sq ft - Minimum)</SelectItem>
                        <SelectItem value="20x30">20' x 30' (600 sq ft)</SelectItem>
                        <SelectItem value="25x25">25' x 25' (625 sq ft)</SelectItem>
                        <SelectItem value="30x30">30' x 30' (900 sq ft)</SelectItem>
                        <SelectItem value="custom">Custom Size</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs mt-2" style={{color: '#D66238', opacity: 0.7}}>
                      Minimum 400 sq ft per animal
                    </p>
                  </div>

                  {/* Post Diameter */}
                  <div>
                    <Label className="text-base mb-2 block" style={{color: '#D66238'}}>Post Diameter *</Label>
                    <Select
                      value={facility.post_diameter || ''}
                      onValueChange={(value) => updateFacility({ post_diameter: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select diameter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="6-inch">6 inches (Minimum)</SelectItem>
                        <SelectItem value="8-inch">8 inches</SelectItem>
                        <SelectItem value="10-inch">10 inches</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Post Spacing */}
                  <div>
                    <Label className="text-base mb-2 block" style={{color: '#D66238'}}>Post Spacing *</Label>
                    <Select
                      value={facility.post_spacing || ''}
                      onValueChange={(value) => updateFacility({ post_spacing: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select spacing" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="6-feet">6 feet</SelectItem>
                        <SelectItem value="8-feet">8 feet (Maximum)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rail Material */}
                  <div>
                    <Label className="text-base mb-2 block" style={{color: '#D66238'}}>Rail Material *</Label>
                    <Select
                      value={facility.rail_material || ''}
                      onValueChange={(value) => updateFacility({ rail_material: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select rail material" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="poles-3inch">Poles (3" min diameter)</SelectItem>
                        <SelectItem value="planks-2x8">Planks (2" x 8")</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rail Count */}
                  <div>
                    <Label className="text-base mb-2 block" style={{color: '#D66238'}}>Number of Rails *</Label>
                    <Select
                      value={facility.rail_count?.toString() || ''}
                      onValueChange={(value) => updateFacility({ rail_count: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select rail count" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 rails (Min for 2" x 8" planks)</SelectItem>
                        <SelectItem value="5">5 rails (Min for 3" poles)</SelectItem>
                        <SelectItem value="6">6 rails</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs mt-2" style={{color: '#D66238', opacity: 0.7}}>
                      No space between rails should exceed 12"
                    </p>
                  </div>

                  {/* Fencing Height */}
                  <div>
                    <Label className="text-base mb-2 block" style={{color: '#D66238'}}>Fence Height *</Label>
                    <Select
                      value={facility.fencing_height || ''}
                      onValueChange={(value) => updateFacility({ fencing_height: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select height" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4.5ft">4.5 feet (Burros)</SelectItem>
                        <SelectItem value="5ft">5 feet (Yearling/Gentled Horses)</SelectItem>
                        <SelectItem value="6ft">6 feet (Ungentled Horses 2+ years)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
              <button
                onClick={goToPrevTab}
                className="px-6 py-3 rounded-full font-semibold transition-all hover:scale-105"
                style={{ backgroundColor: '#E5E7EB', color: '#374151' }}
              >
                ← Back: Location
              </button>
              <button
                onClick={goToNextTab}
                className="px-6 py-3 rounded-full font-semibold text-white transition-all hover:scale-105"
                style={{ backgroundColor: '#D66238' }}
              >
                Next: Resources →
              </button>
            </div>
          </TabsContent>

          {/* RESOURCES TAB */}
          <TabsContent value="resources">
            <div className="space-y-6">
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                <h2 className="text-2xl font-bold mb-6" style={{color: '#D66238'}}>Feed & Water Resources</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Water Source */}
                  <div>
                    <Label className="text-base mb-2 block" style={{color: '#D66238'}}>Water Source *</Label>
                    <Select
                      value={facility.water_source || ''}
                      onValueChange={(value) => updateFacility({ water_source: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select water source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="well-water">Well Water</SelectItem>
                        <SelectItem value="municipal-water">Municipal Water</SelectItem>
                        <SelectItem value="pond-natural">Pond/Natural Water Source</SelectItem>
                        <SelectItem value="rain-collection">Rain Collection</SelectItem>
                        <SelectItem value="hauled-water">Hauled Water</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Hay Type */}
                  <div>
                    <Label className="text-base mb-2 block" style={{color: '#D66238'}}>Hay Type *</Label>
                    <Select
                      value={facility.hay_type || ''}
                      onValueChange={(value) => updateFacility({ hay_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select hay type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alfalfa">Alfalfa</SelectItem>
                        <SelectItem value="orchard-grass">Orchard Grass</SelectItem>
                        <SelectItem value="alfalfa-orchard-mix">Alfalfa/Orchard Grass Mix</SelectItem>
                        <SelectItem value="bermuda">Bermuda</SelectItem>
                        <SelectItem value="bahia">Bahia</SelectItem>
                        <SelectItem value="red-clover-grass-mix">Red Clover/Grass Mix</SelectItem>
                        <SelectItem value="peanut-hay">Peanut Hay</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
              <button
                onClick={goToPrevTab}
                className="px-6 py-3 rounded-full font-semibold transition-all hover:scale-105"
                style={{ backgroundColor: '#E5E7EB', color: '#374151' }}
              >
                ← Back: Corral
              </button>
              <button
                onClick={goToNextTab}
                className="px-6 py-3 rounded-full font-semibold text-white transition-all hover:scale-105"
                style={{ backgroundColor: '#D66238' }}
              >
                Next: Transport →
              </button>
            </div>
          </TabsContent>

          {/* TRANSPORT TAB */}
          <TabsContent value="transport">
            <div className="space-y-6">
              {/* BLM Requirements Info */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#6FCFD3', color: '#fff'}}>
                <h3 className="text-xl font-bold mb-3">BLM Trailer Requirements</h3>
                <ul className="space-y-2">
                  <li>✓ Must be a stock or horse trailer</li>
                  <li>✓ Must have a covered top</li>
                  <li>✓ Must have a rear swing gate</li>
                  <li>✓ Slant trailers acceptable if dividers removed or folded back</li>
                  <li>✓ Drop-ramps acceptable if there is an additional back gate</li>
                  <li>✓ Two-horse trailers approved on case-by-case basis</li>
                  <li>✗ NO one-horse trailers approved</li>
                </ul>
              </div>

              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                <h2 className="text-2xl font-bold mb-6" style={{color: '#D66238'}}>Select Your Trailer Type</h2>
                
                <div>
                  <Label className="text-base mb-2 block" style={{color: '#D66238'}}>
                    <Truck className="w-5 h-5 inline mr-2" style={{color: '#D66238'}} />
                    Trailer Type *
                  </Label>
                  <Select
                    value={facility.trailer_type || ''}
                    onValueChange={(value) => updateFacility({ trailer_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select BLM-approved trailer type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2-horse-stock-covered">2-Horse Stock Trailer (Covered, Rear Swing Gate)</SelectItem>
                      <SelectItem value="2-horse-bumper-covered">2-Horse Bumper Pull (Covered, Rear Swing Gate)</SelectItem>
                      <SelectItem value="2-horse-gooseneck-covered">2-Horse Gooseneck (Covered, Rear Swing Gate)</SelectItem>
                      <SelectItem value="3-horse-slant-no-dividers">3-Horse Slant Load (Dividers Removed/Folded)</SelectItem>
                      <SelectItem value="4-horse-slant-no-dividers">4-Horse Slant Load (Dividers Removed/Folded)</SelectItem>
                      <SelectItem value="stock-trailer-covered">Stock Trailer (Covered, Rear Swing Gate)</SelectItem>
                      <SelectItem value="drop-ramp-back-gate">Drop-Ramp Trailer (With Additional Back Gate)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm mt-3 p-3 rounded-lg" style={{backgroundColor: '#FFF3E0', color: '#D66238'}}>
                    <strong>Important:</strong> All trailers must have a covered top and rear swing gate. Slant trailers must have dividers removed or folded back during transport.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
              <button
                onClick={goToPrevTab}
                className="px-6 py-3 rounded-full font-semibold transition-all hover:scale-105"
                style={{ backgroundColor: '#E5E7EB', color: '#374151' }}
              >
                ← Back: Resources
              </button>
              <button
                onClick={goToNextTab}
                className="px-6 py-3 rounded-full font-semibold text-white transition-all hover:scale-105"
                style={{ backgroundColor: '#D66238' }}
              >
                Next: Layout →
              </button>
            </div>
          </TabsContent>

          {/* LAYOUT BUILDER TAB */}
          <TabsContent value="layout">
            <div className="space-y-6">
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                <h2 className="text-2xl font-bold mb-4" style={{color: '#D66238'}}>Free Drawing Layout Builder</h2>
                <p className="mb-4" style={{color: '#D66238', opacity: 0.8}}>
                  Design your facility layout using the drawing tools. Draw structures, add labels, and save your design.
                </p>
                
                {/* Drawing Toolbar */}
                <div className="mb-4 p-3 rounded-lg flex flex-wrap gap-2 items-center" style={{backgroundColor: '#FCFCF8', border: '1px solid #6FCFD3'}}>
                  {/* Tools */}
                  <div className="flex gap-1 flex-wrap">
                    <Button
                      onClick={() => setCurrentTool(DRAWING_TOOLS.SELECT)}
                      variant={currentTool === DRAWING_TOOLS.SELECT ? 'default' : 'outline'}
                      size="sm"
                      style={currentTool === DRAWING_TOOLS.SELECT ? {backgroundColor: '#6FCFD3', color: '#fff'} : {}}
                      title="Select/Move"
                    >
                      <Move className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setCurrentTool(DRAWING_TOOLS.PEN)}
                      variant={currentTool === DRAWING_TOOLS.PEN ? 'default' : 'outline'}
                      size="sm"
                      style={currentTool === DRAWING_TOOLS.PEN ? {backgroundColor: '#6FCFD3', color: '#fff'} : {}}
                      title="Pen"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setCurrentTool(DRAWING_TOOLS.TEXT)}
                      variant={currentTool === DRAWING_TOOLS.TEXT ? 'default' : 'outline'}
                      size="sm"
                      style={currentTool === DRAWING_TOOLS.TEXT ? {backgroundColor: '#6FCFD3', color: '#fff'} : {}}
                      title="Text"
                    >
                      <Type className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setCurrentTool(DRAWING_TOOLS.ERASER)}
                      variant={currentTool === DRAWING_TOOLS.ERASER ? 'default' : 'outline'}
                      size="sm"
                      style={currentTool === DRAWING_TOOLS.ERASER ? {backgroundColor: '#6FCFD3', color: '#fff'} : {}}
                      title="Eraser"
                    >
                      <Eraser className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="h-6 w-px bg-gray-300"></div>

                  {/* Colors */}
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-semibold mr-1" style={{color: '#D66238'}}>Color:</span>
                    {COLORS.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setCurrentColor(color.value)}
                        className="w-6 h-6 rounded border-2 hover:scale-110 transition-transform"
                        style={{
                          backgroundColor: color.value,
                          borderColor: currentColor === color.value ? '#D66238' : '#ccc'
                        }}
                        title={color.name}
                      />
                    ))}
                  </div>

                  <div className="h-6 w-px bg-gray-300"></div>

                  {/* Size */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold" style={{color: '#D66238'}}>Size:</span>
                    <Select value={brushSize.toString()} onValueChange={(val) => setBrushSize(parseInt(val))}>
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Small</SelectItem>
                        <SelectItem value="2">Medium</SelectItem>
                        <SelectItem value="4">Large</SelectItem>
                        <SelectItem value="6">X-Large</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="h-6 w-px bg-gray-300"></div>

                  {/* Grid Toggle */}
                  <Button
                    onClick={() => setShowGrid(!showGrid)}
                    variant={showGrid ? 'default' : 'outline'}
                    size="sm"
                    style={showGrid ? {backgroundColor: '#6FCFD3', color: '#fff'} : {}}
                    title={showGrid ? 'Hide Grid' : 'Show Grid'}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h16M4 12h16M4 19h16" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v18M15 3v18" />
                    </svg>
                  </Button>

                  <div className="h-6 w-px bg-gray-300"></div>

                  {/* Actions */}
                  <div className="flex gap-1">
                    <Button onClick={undo} disabled={historyStep <= 0} variant="outline" size="sm" title="Undo">
                      <Undo className="w-4 h-4" />
                    </Button>
                    <Button onClick={redo} disabled={historyStep >= history.length - 1} variant="outline" size="sm" title="Redo">
                      <Redo className="w-4 h-4" />
                    </Button>
                    <Button onClick={clearCanvas} variant="outline" size="sm" title="Clear">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {selectedLayer && (
                      <Button 
                        onClick={deleteSelectedLayer} 
                        variant="destructive"
                        size="sm"
                        title="Delete selected layer"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete Layer
                      </Button>
                    )}
                  </div>

                  <div className="h-6 w-px bg-gray-300"></div>

                  {/* Save/Download */}
                  <div className="flex gap-1 ml-auto">
                    <Button onClick={downloadImage} variant="outline" size="sm" style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                    <Button onClick={saveLayoutDrawing} size="sm" style={{backgroundColor: '#6FCFD3', color: '#fff'}}>
                      <Save className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                  </div>
                </div>

                {/* Canvas */}
                <div className="border-2 rounded-lg overflow-hidden w-full" style={{borderColor: '#6FCFD3'}}>
                  <canvas
                    ref={canvasRef}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    onTouchCancel={stopDrawing}
                    className="bg-white touch-none w-full cursor-crosshair"
                    style={{display: 'block', maxWidth: '100%', height: 'auto'}}
                  />
                </div>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
              <button
                onClick={goToPrevTab}
                className="px-6 py-3 rounded-full font-semibold transition-all hover:scale-105"
                style={{ backgroundColor: '#E5E7EB', color: '#374151' }}
              >
                ← Back: Transport
              </button>
              <button
                onClick={saveFacility}
                disabled={loading}
                className="px-6 py-3 rounded-full font-semibold text-white transition-all hover:scale-105"
                style={{ backgroundColor: '#10B981' }}
              >
                {loading ? 'Saving...' : '✓ Save & Complete'}
              </button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Requirements Checklist */}
        <div className="mt-8 p-6 rounded-2xl" style={{backgroundColor: '#FCFCF8'}}>
          <h3 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>Adoption Requirements Checklist</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2" style={{color: '#D66238'}}>Location & Shelter</h4>
              <div className="space-y-2">
                <ChecklistItem label="State Selected" completed={!!facility.state} />
                <ChecklistItem label="Shelter Type" completed={!!facility.shelter_type} />
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-2" style={{color: '#D66238'}}>Corral Specifications</h4>
              <div className="space-y-2">
                <ChecklistItem label="Corral Material" completed={!!facility.corral_material} />
                <ChecklistItem label="Corral Dimensions" completed={!!facility.corral_dimensions} />
                <ChecklistItem label="Post Diameter" completed={!!facility.post_diameter} />
                <ChecklistItem label="Post Spacing" completed={!!facility.post_spacing} />
                <ChecklistItem label="Rail Material" completed={!!facility.rail_material} />
                <ChecklistItem label="Rail Count" completed={!!facility.rail_count} />
                <ChecklistItem label="Fence Height" completed={!!facility.fencing_height} />
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-2" style={{color: '#D66238'}}>Resources</h4>
              <div className="space-y-2">
                <ChecklistItem label="Water Source" completed={!!facility.water_source} />
                <ChecklistItem label="Hay Type" completed={!!facility.hay_type} />
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-2" style={{color: '#D66238'}}>Transport & Layout</h4>
              <div className="space-y-2">
                <ChecklistItem label="BLM-Approved Trailer" completed={!!facility.trailer_type} />
                <ChecklistItem label="Facility Layout" completed={!!facility.layout?.items?.length} />
              </div>
            </div>
          </div>
        </div>

        {/* Text Input Dialog */}
        {showTextInput && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full mx-4">
              <h3 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>
                Add Text Label
              </h3>
              <Input
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Enter text (e.g., 'Barn', 'Pasture')"
                className="mb-4"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') addText();
                }}
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowTextInput(false);
                    setTextInput('');
                    setTextPosition(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={addText}
                  className="flex-1"
                  style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                >
                  Add Text
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ChecklistItem = ({ label, completed }) => {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
        style={{backgroundColor: completed ? '#6FCFD3' : '#e0e0e0'}}
      >
        {completed && <Check className="w-3 h-3" style={{color: '#fff'}} />}
      </div>
      <span className="text-sm" style={{color: '#D66238', textDecoration: completed ? 'line-through' : 'none'}}>
        {label}
      </span>
    </div>
  );
};

export default FacilityBuilder;
